import { Box } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';

import { WIDTH_MODAL_COMPONENTS } from '../../../../constants/addressBook';
import { INITIAL_PAGE_SIZE } from '../../../../constants/table';
import { IRow } from '../../../../types/addressBook';
import notification from '../../../../utils/notification';
import NotificationAlert, { AlertDescription } from '../../../_mui/_alerts_/NotificationAlert';
import { RepairTableContent, SearchAndFilter } from '../components';
import { useFilteredRows, useRepairTableColumns } from '../hooks';
import {
  dataGridTableStyles,
  editingCellsStyles,
  focusedCellsStyles,
  stickyColumnsStyles,
} from '../styles/RepairAddressesStep.styles';
import { useUploadModal } from '../UploadModalContext';

const RepairAddressesStep: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const {
    rows,
    setRows,
    deletedRowsStackRef,
    mappedColumns,
    invalidRows,
    showIssuesOnly,
    setShowIssuesOnly,
    handleShowAllRows,
    clearModalState,
    hasRows,
    profile,
    isCellEditable,
  } = useUploadModal();

  const handleProcessRowUpdate = useCallback(
    (newRow: IRow) => {
      setRows((prev) => prev.map((r) => (r.id === newRow.id ? newRow : r)));
      return newRow;
    },
    [setRows],
  );

  const handleUndoDelete = useCallback(() => {
    const lastDeletedRow = deletedRowsStackRef.current?.pop();
    if (lastDeletedRow) {
      setRows((prev) => [...prev, lastDeletedRow]);

      notification.success(undefined, {
        content: 'Row has been restored to the end of the list.',
      });
    }
  }, [setRows, deletedRowsStackRef]);

  const handleDeleteRow = useCallback(
    (id: string) => {
      const rowIndex = rows.findIndex((r) => r.id === id);
      if (rowIndex !== -1) {
        const rowToDelete = rows[rowIndex];
        deletedRowsStackRef.current?.push(rowToDelete);
        setRows((prev) => prev.filter((r) => r.id !== id));

        notification.info(undefined, {
          content: 'Row deleted',
          options: {
            action: {
              label: 'UNDO',
              onClick: handleUndoDelete,
            },
          },
        });
      }
    },
    [rows, setRows, handleUndoDelete, deletedRowsStackRef],
  );

  const handleSearchQueryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const columns = useRepairTableColumns({
    mappedColumns,
    invalidRows,
    onDeleteRow: handleDeleteRow,
    columnWidths: profile.columnWidths,
    isCellEditable,
  });

  const firstColumnId = profile.columns[0]?.id ?? '';

  const { filteredRows, hasNoIssues } = useFilteredRows({
    rows,
    invalidRows,
    showIssuesOnly,
    searchQuery,
  });

  const paginationModel = useMemo(() => ({ page: 0, pageSize: INITIAL_PAGE_SIZE }), []);

  const allIssuesFixed = useMemo(() => {
    return hasRows && showIssuesOnly && hasNoIssues;
  }, [hasRows, showIssuesOnly, hasNoIssues]);

  const noResultsForSearch = useMemo(() => {
    return hasRows && filteredRows.length === 0;
  }, [hasRows, filteredRows]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ mt: 3, mb: 3, mx: 'auto', maxWidth: WIDTH_MODAL_COMPONENTS, width: '100%' }}>
        <NotificationAlert type={profile.copy.repairAlert.type}>
          <AlertDescription>{profile.copy.repairAlert.description}</AlertDescription>
        </NotificationAlert>
      </Box>

      <SearchAndFilter
        searchQuery={searchQuery}
        onSearchChange={handleSearchQueryChange}
        showIssuesOnly={showIssuesOnly}
        onToggleIssuesOnly={setShowIssuesOnly}
      />

      <Box sx={{ mt: 2, mb: 2, width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <RepairTableContent
          allIssuesFixed={allIssuesFixed}
          noResultsForSearch={noResultsForSearch}
          hasRows={hasRows}
          searchQuery={searchQuery}
          filteredRows={filteredRows}
          columns={columns}
          paginationModel={paginationModel}
          dataGridTableStyles={dataGridTableStyles}
          stickyColumnsStyles={stickyColumnsStyles(firstColumnId)}
          focusedCellsStyles={focusedCellsStyles}
          editingCellsStyles={editingCellsStyles(firstColumnId)}
          handleShowAllRows={handleShowAllRows}
          clearModalState={clearModalState}
          handleProcessRowUpdate={handleProcessRowUpdate}
          columnWidths={profile.columnWidths}
          copy={profile.copy}
          isCellEditable={isCellEditable}
        />
      </Box>
    </Box>
  );
};

export default RepairAddressesStep;
